/**
 * PersonaBand — the « Persona » composer source (owner, 2026-11-02): pick a Brand Knowledge persona,
 * and « Générer » writes the questions THAT person would ask an AI assistant, in its voice, from its
 * description and traits (`POST …/regenerate { brandPersonaId }` → `generateFromPersona`), instead of
 * an axis and its terms. The proposals land in the editable draft; « Vérifier » classifies each one
 * onto its axis. Shares the generation cooldown and the count / language with Générer.
 */
import { useMemo, useState } from 'react';
import { Box, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import type { BrandPersona } from '@traaker/shared';
import type { GenState } from './composerModel';
import { GEN_COOLDOWN_MS } from './composerModel';
import { brandQueriesApi } from '../../services/brand-queries';
import { Pick } from './Pick';
import { GenerateButton, generationLanguages } from './GenerateBand';

export interface PersonaBandProps {
  gen: GenState;
  brandId: string;
  languages: { code: string; label: string }[];
  personas: BrandPersona[];
  cooldownUntil: number | null;
  done: boolean;
  onChange: (gen: GenState) => void;
  onDraft: (lines: string[]) => void;
  onCooldown: (until: number) => void;
}

export function PersonaBand({ gen, brandId, languages, personas, cooldownUntil, done, onChange, onDraft, onCooldown }: PersonaBandProps) {
  const { t, i18n } = useTranslation();
  const [pick, setPick] = useState<'persona' | 'language' | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const languageOptions = useMemo(() => generationLanguages(languages, i18n.language), [languages, i18n.language]);
  const persona = personas.find((p) => p.brandPersonaId === gen.personaId) ?? null;

  const run = async () => {
    if (loading || !persona) return;
    setLoading(true);
    setError(false);
    try {
      // The axis is required by the route and ignored with a persona.
      const res = await brandQueriesApi.regenerate(brandId, gen.axis, {
        count: gen.count, languages: [gen.language], brandPersonaId: persona.brandPersonaId, studio: true,
      });
      onCooldown(Date.now() + GEN_COOLDOWN_MS);
      onDraft((res.proposals ?? []).map((p) => p.text).filter(Boolean) as string[]);
    } catch (err: unknown) {
      const e = err as { body?: { code?: string; retryAfterSeconds?: number } };
      if (e?.body?.code === 'GEN_COOLDOWN' && typeof e.body.retryAfterSeconds === 'number') {
        onCooldown(Date.now() + e.body.retryAfterSeconds * 1000);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const open = (p: 'persona' | 'language') => { setSearch(''); setPick(p); };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>{t('promptStudio.gen.persona')}</Box>
        <Chip
          label={persona ? `👤 ${persona.name}` : t('promptStudio.gen.choosePersona')}
          size="small"
          onClick={() => open('persona')}
          onDelete={() => open('persona')}
          deleteIcon={<ExpandMoreIcon />}
          sx={{
            fontSize: 12, color: '#7c3aed',
            bgcolor: persona ? 'rgba(124,58,237,.08)' : 'transparent',
            border: persona ? '1px solid rgba(124,58,237,.25)' : '1px dashed rgba(124,58,237,.45)',
          }}
        />
      </Box>
      <Box sx={{ fontSize: 11.5, color: 'text.secondary' }}>
        {persona?.description && (
          <Box component="span" sx={{ fontStyle: 'italic' }}>
            « {persona.description.slice(0, 200)}{persona.description.length > 200 ? '…' : ''} » ·{' '}
          </Box>
        )}
        {t('promptStudio.gen.byPersonaNote')}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>{t('promptStudio.gen.language')}</Box>
        <Chip
          label={languageOptions.find((l) => l.code === gen.language)?.label ?? gen.language}
          size="small"
          onClick={() => open('language')}
          onDelete={() => open('language')}
          deleteIcon={<ExpandMoreIcon />}
          sx={{ fontSize: 12, bgcolor: 'rgba(124,58,237,.08)', color: '#7c3aed', border: '1px solid rgba(124,58,237,.25)' }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>{t('promptStudio.gen.count')}</Box>
        <ToggleButtonGroup value={gen.count} exclusive onChange={(_, v) => v && onChange({ ...gen, count: v })} size="small">
          {([3, 5, 10] as const).map((n) => (
            <ToggleButton key={n} value={n} sx={{ fontSize: 12, px: 1.5, py: 0.375 }}>{n}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <GenerateButton cooldownUntil={cooldownUntil} done={done} loading={loading} disabled={!persona} onRun={run} />
      {error && <Box sx={{ fontSize: 12, color: '#b45309' }}>{t('promptStudio.gen.failed')}</Box>}

      <Pick
        open={pick === 'persona'}
        kind="persona"
        selected={gen.personaId ?? ''}
        onToggle={(id) => { onChange({ ...gen, personaId: id || null }); setPick(null); }}
        onClose={() => setPick(null)}
        personas={personas}
        searchQuery={search}
        onSearchChange={setSearch}
      />
      <Pick
        open={pick === 'language'}
        kind="language"
        selected={gen.language}
        onToggle={(code) => { onChange({ ...gen, language: code }); setPick(null); }}
        onClose={() => setPick(null)}
        languageOptions={languageOptions}
        searchQuery={search}
        onSearchChange={setSearch}
      />
    </Box>
  );
}
